import React, { FC, useRef, useState, useMemo, useEffect } from 'react';
import filter from 'lodash/filter';
import find from 'lodash/find';
import get from 'lodash/get';
import moment from 'moment';
import orderBy from 'lodash/orderBy';
import styled, { css } from 'styled-components';
import { useDispatch } from 'react-redux';

import ADAudioPlayer, {
  TimeRemaining,
  TrackLabel,
  PauseControls,
  StyledIcon,
} from '@ad-scripts/presentation/components/common/audio_player';
import ActionsModal, { ModalContent, Actions } from 'src/presentation/components/pages/shared/modals/ActionsModal';
import AudioSaver from 'src/presentation/components/pages/shared/AudioSaver';
import PlainTextButton from 'src/presentation/components/pages/shared/PlainTextButton';
import RadioButton from '@ad-scripts/presentation/components/common/radio_button';
import TableLayout from 'src/presentation/components/layouts/table/TableLayout';
import TableList from 'src/presentation/components/pages/shared/TableList';
import {
  StyledTD,
  StyledTR,
  EmptyStateWrapper,
  RowActionContainer,
  StyledTH,
} from 'src/presentation/components/pages/shared/Table';
import analytics from '@ad-scripts/lib/analytics';
import ExternalLink from 'src/presentation/components/pages/shared/ExternalLink';
import recordingsResource from 'src/actions/ivrs/recordingsResource';
import { useResourceValues } from 'src/presentation/hooks/useResource';
import { Button, ButtonTypes } from 'src/presentation/components/agent_desktop_components';
import { StyledConfirmOrCancelModal } from 'src/presentation/components/pages/shared/rowActions/StyledConfirmOrCancelModal';
import { THead } from '@ad-scripts/presentation/components/common/table';
import { getRecordingFileName } from 'src/presentation/components/pages/shared/utils/Utils';
import { toastError } from 'src/actions/toast';

type RecordingLibraryType = {
  currentRecordingUrl: string;
  onCancel: Function;
  onSave: Function;
  selectionMode?: boolean;
};

const ACTION_SELECT = 'ACTION_SELECT';
const ACTION_CANCEL = 'ACTION_CANCEL';
const dataAidPrefix = 'recording-library';

// covers the following template format: "prefixString{{...}}postfixString", e.g.
// "directions-{{replace '+' '' ._companyNumber}}.mp3"
// where `directions-` is prefix and `.mp3` is postfix
// https://regex101.com/r/qkFvTx/1
const FILENAME_WITH_TEMPLATE_REGEX = /^(?<prefix>.+)?\{\{.+\}\}(?<postfix>.+)$/;

const RecordingLibrary: FC<RecordingLibraryType> = ({ currentRecordingUrl, onCancel, onSave, selectionMode }) => {
  const dispatch = useDispatch();
  const actionRef = useRef<HTMLInputElement | null>(null);
  const { loadingState, values = [] } = useResourceValues(recordingsResource);
  const [recordingUrlToReplace, setRecordingUrlToReplace] = useState<string>('');
  const [timeStamp] = useState<number>(new Date().getTime());
  const [recordingVersions, setRecordingVersions] = useState({});
  const [isConfirmReplaceModalVisible, setIsConfirmReplaceModalVisible] = useState<boolean>(false);
  const [highlightedItemUrl, setHighlightedItemUrl] = useState<string>('');
  const [highlightedTimeout, setHighlightedTimeout] = useState<number>();

  const fileNameFromUrl = getRecordingFileName(currentRecordingUrl);

  // fileNameWithTemplate = null, if it's a regular recording
  // fileNameWithTemplate = { templatePrefix: string, templatePostfix: string }, if it's a templated one
  const fileNameWithTemplate = useMemo(() => {
    const templatedFileName = fileNameFromUrl.match(FILENAME_WITH_TEMPLATE_REGEX);

    if (!templatedFileName) {
      return null;
    }

    return {
      templatePrefix: get(templatedFileName, 'groups.prefix', ''),
      templatePostfix: get(templatedFileName, 'groups.postfix', ''),
    };
  }, [fileNameFromUrl]);

  const isTemplatedMode = !!fileNameWithTemplate;
  const recordings = getRecordings(currentRecordingUrl, fileNameWithTemplate, values);
  const [selectedRecording, setSelectedRecording] = useState(currentRecordingUrl);
  const [currentTrackToPlay, setCurrentTrackToPlay] = useState<string>('');

  const columnConfig = [
    { attr: 'id', label: 'Name', customColumnStyle: () => ({ minWidth: '350px' }) },
    { attr: 'lastUpdatedAt', label: 'Last updated' },
  ];

  // prevents memory leak if the component is closed before the timeout ends.
  useEffect(() => {
    return () => {
      highlightedTimeout && clearTimeout(highlightedTimeout);
    };
  }, [highlightedTimeout]);

  const filterFunc = (items, searchString) =>
    searchString ? filter(items, ({ id = '' }) => id.toLowerCase().indexOf(searchString.toLowerCase()) > -1) : items;

  const onNewRecordingButtonClick = () => {
    actionRef!.current!.click();
  };

  const actionButton = (
    <Button data-aid="upload-new-recording" type={Button.Types.SECONDARY} onClick={onNewRecordingButtonClick}>
      Upload New Recording
    </Button>
  );

  const emptyStateRender = (
    <EmptyStateWrapper data-aid="recording-library-list-noItemsMessage">
      <StyledHeader>
        There are no recordings <br />
        in the library right now
      </StyledHeader>
      <StyledTextButton onClick={onNewRecordingButtonClick}>Upload New Recording</StyledTextButton>
    </EmptyStateWrapper>
  );

  const onNewRecordAdded = newRecordingUrl => {
    if (newRecordingUrl) {
      dispatch(recordingsResource.effects.fetchAll());

      setHighlightedItemUrl(newRecordingUrl);

      // turns off 'row highlight' animation
      // using of window.setTimeout is necessary https://github.com/microsoft/TypeScript/issues/842
      const timeoutId = window.setTimeout(() => setHighlightedItemUrl(''), 4000);
      setHighlightedTimeout(timeoutId);
    }
  };

  const fileExtensionToSelect = useMemo(() => {
    if (!isTemplatedMode) {
      return '';
    }

    const extension = get(fileNameWithTemplate, 'templatePostfix', '').split('.').pop();

    return extension ? `.${extension}` : '';
  }, [fileNameWithTemplate, isTemplatedMode]);

  const onReplaceConfirm = () => {
    setIsConfirmReplaceModalVisible(false);
    actionRef!.current!.click();
  };

  const onReplaceCancel = () => setIsConfirmReplaceModalVisible(false);

  const onRecordReplaced = replacedRecordingUrl => {
    setRecordingUrlToReplace('');

    if (replacedRecordingUrl) {
      setHighlightedItemUrl(replacedRecordingUrl);

      // turns off 'row highlight' animation
      // using of window.setTimeout is necessary https://github.com/microsoft/TypeScript/issues/842
      const timeoutId = window.setTimeout(() => setHighlightedItemUrl(''), 4000);
      setHighlightedTimeout(timeoutId);

      setRecordingVersions({
        ...recordingVersions,
        [replacedRecordingUrl]: (recordingVersions[replacedRecordingUrl] || 0) + 1,
      });
    }
  };

  const audioSaver = (
    <AudioSaver
      allRecordings={recordings}
      fileExt={fileExtensionToSelect}
      onSave={recordingUrlToReplace ? onRecordReplaced : onNewRecordAdded}
      recordingUrl={recordingUrlToReplace}
      ref={actionRef}
      templated={fileNameWithTemplate}
    />
  );

  const onAudioLoadingError = errorDetails => {
    if (errorDetails) {
      const {
        detail = "Recording could not be played. Confirm that the recording is a supported format and isn't a corrupted file.",
        trackLabel = '',
      } = errorDetails;
      dispatch(toastError(`${detail} ${trackLabel}`));
    }
  };

  function renderRow({ id, lastUpdatedAt, url }) {
    const recordingVersion = get(recordingVersions, url, 0);
    const isHighlighted = highlightedItemUrl === url;

    const audioPlayer = (
      <PlayerContainer onClick={() => setCurrentTrackToPlay(url)}>
        <StyledAudioPlayer
          data-aid={`player-${id}`}
          enablePlayOnError
          isPlaying={currentTrackToPlay === url}
          onError={onAudioLoadingError}
          trackLabel={id}
          url={`${url}?timestamp=${timeStamp}&version=${recordingVersion}`}
        />
      </PlayerContainer>
    );

    const onSelectRecording = () => setSelectedRecording(url);
    const onReplaceButtonClick = () => {
      setRecordingUrlToReplace(url);
      setIsConfirmReplaceModalVisible(true);
    };

    return (
      <RecordingRow
        audioPlayer={audioPlayer}
        id={id}
        isChecked={selectedRecording === url}
        isTemplatedMode={isTemplatedMode}
        isHighlighted={isHighlighted}
        key={url}
        lastUpdatedAt={lastUpdatedAt}
        onReplaceButtonClick={onReplaceButtonClick}
        onSelectRecording={onSelectRecording}
        url={url}
      />
    );
  }

  const pageName = isTemplatedMode ? 'Recording Library for Dynamic Node' : 'Recording Library';
  const description = isTemplatedMode ? (
    <span>
      This node uses this template to reference which recording to play, based on the number a customer calls in from.
      <br />
      You can upload new recordings in the correct format or replace the content of an already existing recording.
      <br />
      {currentRecordingUrl ? (
        <span>
          The template is <strong>{getRecordingFileName(currentRecordingUrl)}</strong>
        </span>
      ) : (
        ''
      )}
    </span>
  ) : (
    <span>
      Select or upload a new recording in MP3 format. Learn more about recordings in our{' '}
      <ExternalLink
        label="Help Docs."
        to="https://help.gladly.com/docs/update-node-recordings#updatereplace-a-node-recording"
      />
    </span>
  );

  const isLoading = loadingState !== 'succeeded';

  const recordingsTable = (
    <StyledTableList
      actions={actionButton}
      columnConfig={columnConfig}
      dataAid="recording-library-list"
      description={description}
      emptyStateRender={emptyStateRender}
      filterFunc={filterFunc}
      fullHeight={isTemplatedMode}
      isLoading={isLoading}
      items={recordings}
      pageName={pageName}
      renderRow={renderRow}
    />
  );

  const submitText = selectionMode ? 'Select' : 'Save';

  const sendAnalyticsData = (type: string): void => {
    const fileName = getRecordingFileName(selectedRecording);
    if (type === ACTION_SELECT) {
      analytics.track('Admin - Recording Library - Selected New File', { fileName });
    }

    if (type === ACTION_CANCEL) {
      analytics.track('Admin - Recording Library - Closed Modal');
    }
  };

  const handleButtonSubmit = () => {
    onSave(selectedRecording);
    sendAnalyticsData(ACTION_SELECT);
  };

  const handleButtonCancel = () => {
    onCancel();
    sendAnalyticsData(ACTION_CANCEL);
  };

  return (
    <StyledActionsModal
      data-aid={dataAidPrefix}
      disableSubmitButton={currentRecordingUrl === selectedRecording}
      hideActions={isTemplatedMode}
      onCancel={handleButtonCancel}
      onSubmit={handleButtonSubmit}
      submitText={submitText}
    >
      {recordingsTable}
      {audioSaver}
      {isConfirmReplaceModalVisible && (
        <ConfirmReplaceModal
          handleCancel={onReplaceCancel}
          handleSubmit={onReplaceConfirm}
          recordingUrlToReplace={recordingUrlToReplace}
        />
      )}
    </StyledActionsModal>
  );
};

const RecordingRow = ({
  audioPlayer,
  id,
  isChecked,
  isHighlighted,
  isTemplatedMode,
  onReplaceButtonClick,
  onSelectRecording,
  lastUpdatedAt,
  url,
}) => {
  const rowRef = useRef<HTMLAnchorElement>(null);

  const lastUpdatedText = lastUpdatedAt ? moment(lastUpdatedAt).format('MMM DD, YYYY') : '';

  const replaceRecordingAction = isTemplatedMode ? (
    <RowActionContainer>
      <ActionButton onClick={onReplaceButtonClick}>Replace Content</ActionButton>
    </RowActionContainer>
  ) : null;

  const actions = (
    <ActionsContent>
      {replaceRecordingAction}
      {audioPlayer}
    </ActionsContent>
  );

  useEffect(() => {
    if (isHighlighted && rowRef.current) {
      // { behavior: 'smooth' } was removed due to a Chrome issue that breaks scrolling
      // https://bugs.chromium.org/p/chromium/issues/list?q=scrollintoview%20smooth
      rowRef.current.scrollIntoView({ block: 'center' });
    }
  }, [isHighlighted]);

  return (
    <StyledRow data-aid={`${dataAidPrefix}-row`} isHighlighted={isHighlighted} key={url} ref={rowRef}>
      <NameTD
        data-aid={`${dataAidPrefix}-cell-${id}`}
        key={`${dataAidPrefix}-cell-${id}`}
        onClick={onSelectRecording}
        templatedMode={isTemplatedMode}
      >
        {isTemplatedMode ? (
          id
        ) : (
          <RadioButton
            dataAid={`${dataAidPrefix}-cell-${id}-radio`}
            isChecked={isChecked}
            key={`${dataAidPrefix}-${id}`}
            label={id}
            value={url}
          />
        )}
      </NameTD>
      <LastUpdatedTD
        data-aid={`${dataAidPrefix}-cell-${id}-lastUpdated`}
        key={`${dataAidPrefix}-cell-${id}-lastUpdated`}
        onClick={onSelectRecording}
        templatedMode={isTemplatedMode}
      >
        {lastUpdatedText}
      </LastUpdatedTD>
      <StyledTD>{actions}</StyledTD>
    </StyledRow>
  );
};

const getRecordings = (currentRecordingUrl, fileNameWithTemplate, values) => {
  const sortedValues = orderBy(values, ['lastUpdatedAt'], ['desc']);

  // templated list
  if (fileNameWithTemplate) {
    const { templatePrefix, templatePostfix } = fileNameWithTemplate;

    return filter(
      sortedValues,
      ({ id: fileName = '' }) => fileName.startsWith(templatePrefix) && fileName.endsWith(templatePostfix)
    );
  }

  // regular list
  const currentRecordingItem = currentRecordingUrl && find(sortedValues, ({ url }) => url === currentRecordingUrl);

  // the current item should always go first
  return currentRecordingItem
    ? [currentRecordingItem, ...filter(sortedValues, ({ url }) => url !== currentRecordingUrl)]
    : sortedValues;
};

const ConfirmReplaceModal = ({ recordingUrlToReplace, handleCancel, handleSubmit }) => {
  const recordingName = getRecordingFileName(recordingUrlToReplace);

  return (
    <StyledConfirmOrCancelModal
      data-aid="confirm-replace"
      onCancel={handleCancel}
      onSubmit={handleSubmit}
      submitButtonType={ButtonTypes.PRIMARY}
      submitText="Replace"
      text="This recording will change automatically without Publish."
      title={`You are replacing the content of ${recordingName}`}
    />
  );
};

const StyledHeader = styled.h3`
  text-align: center;
`;

const StyledTextButton = styled(Button).attrs({ type: Button.Types.TEXT })`
  text-decoration: underline;
`;

const StyledTableList = styled(TableList)`
  ${TableLayout} {
    border-radius: 0;
    box-shadow: none;
    height: ${p => (p.fullHeight ? '520px' : '480px')};
    margin: 8px 0 0;
    overflow-y: auto;

    @media (max-width: 1280px) {
      margin-right: 0;
      width: 100%;
    }
  }

  ${THead} {
    position: sticky;
    top: 0;

    ${StyledTH}:last-child {
      width: 105px;
    }
  }
`;

const blinkingRow = css`
  background: ${p => p.theme.colors.white};
  animation: blinking 0.5s ease-in-out 8;
  @keyframes blinking {
    50% {
      background: ${p => p.theme.colors.green100};
    }
  }
`;

const StyledRow = styled(StyledTR)`
  ${p => (p.isHighlighted ? blinkingRow : '')}
`;

const NameTD = styled(StyledTD)`
  &:hover {
    cursor: ${p => (p.templatedMode ? 'auto' : 'pointer')};
  }

  label {
    margin-bottom: 0;

    span {
      margin-left: 4px;
    }
  }
`;

const LastUpdatedTD = styled(StyledTD)`
  &:hover {
    cursor: ${p => (p.templatedMode ? 'auto' : 'pointer')};
  }
`;

const StyledActionsModal = styled(ActionsModal)`
  ${ModalContent} {
    padding: 0 0 ${p => (p.hideActions ? '0' : '50px')};
  }

  ${Actions} {
    ${p => (p.hideActions ? 'display: none;' : '')}
  }
`;

const ActionsContent = styled.div`
  align-items: center;
  display: flex;
  justify-content: flex-end;
`;

const PlayerContainer = styled.div`
  margin: 0;
  min-width: 80px;
`;

const ActionButton = styled(PlainTextButton)`
  visibility: hidden;
`;

const StyledAudioPlayer = styled(ADAudioPlayer)`
  justify-content: flex-start;
  flex-direction: row-reverse;

  ${StyledIcon} {
    svg {
      fill: ${p => p.theme.colors.gray600};
    }
  }

  ${PauseControls} {
    svg {
      fill: ${p => p.theme.colors.green400};
    }
  }

  ${TimeRemaining} {
    margin-right: 8px;
  }

  ${TrackLabel} {
    display: none;
  }
`;

export default RecordingLibrary;
